<template>
<v-dialog v-model="dialog" max-width="600px">
  <template v-slot:activator="{ on }">
    <v-btn v-if="!noButton" class="button-style-blue" v-on="on" dark>Add Payment <v-icon dark right>add</v-icon>
    </v-btn>
  </template>
  <v-card>
    <v-form
      @submit.prevent="submitPaymentForm"
      ref="form"
      lazy-validation
    >
      <v-card-title>
        <span class="headline">{{ this.newPayment.id ? 'Edit' : 'Add'}} Payment Form ({{student.full_name}})
        </span>
      </v-card-title>
      <v-card-text>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs12>
              <v-autocomplete
                :readonly="!!this.newPayment.id"
                :items="enrollments"
                item-text="display"
                return-object
                v-model="selectedEnrollment"
                color="#091D3B"
                label="Student *"
                :rules="[v => !!v.id || 'Student is required']"
              >
              </v-autocomplete>
            </v-flex>
            <v-flex xs12 md6>
              <v-select
                v-model="newPayment.method"
                :items="paymentMode"
                color="#091D3B"
                label="Payment Mode *"
                :rules="[v => !!v || 'Payment Mode is required']"
              >
              </v-select>
            </v-flex>
            <v-flex xs12 md6 class="box-white">
              <v-menu
                v-model="menu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="newPayment.collected_date"
                    height="32"
                    label="Collected Date *"
                    readonly
                    color="#091D3B"
                    v-on="on"
                    :rules="[v => !!v || 'Collected Date is required']"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="newPayment.collected_date"
                  @input="menu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12 md6>
            <v-currency-field
              prefix="₱"
              v-model="newPayment.amount"
              color="#091D3B"
              label="Amount *"
              :type="'number'"
              :messages="amountInWords"
              :rules="
                [
                  v => !!v || 'Amount is required',
                  v => parseFloat(v) > 0 || 'Amount cannot be less than or equal to 0',
                ]
              "
              > </v-currency-field>
            </v-flex>
            <v-flex xs12 md6>
              <v-text-field
                v-model="newPayment.collected_by"
                color="#091D3B"
                label="Collected By *"
                :rules="[v => !!v || 'Collected By is required']"
              >
              </v-text-field>
            </v-flex>
            <v-flex xs12 md6>
              <v-text-field
                v-model="newPayment.payment_for"
                color="#091D3B"
                label="Payment For *"
                :type="'string'"
                :rules="[v => !!v || 'Payment For is required']"
              >
              </v-text-field>
            </v-flex>
            <v-flex xs12 md6>
              <v-textarea
                v-model="newPayment.remarks"
                color="#091D3B"
                rows="1"
                auto-grow
                label="Remarks"
              >
              </v-textarea>
            </v-flex>
            <v-flex xs12 md6>
            <v-currency-field
              prefix="₱"
              color="#091D3B"
              label="Old Balance"
              :value="oldBalance"
              disabled
              >
              </v-currency-field>
            </v-flex>
            <v-flex xs12 md6>
            <v-currency-field
              prefix="₱"
              color="#091D3B"
              label="New Balance"
              :value="newBalance"
              disabled
              >
              </v-currency-field>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="#091D3B" outline @click="closeDialog">Close</v-btn>
        <v-btn type="submit" class="button-style-blue">Save</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</v-dialog>
</template>
<script>
import { mapState, mapActions } from 'vuex';
import numberToWords from 'number-to-words'

export default {
  name: 'PaymentModal',
  props: {
    successCallback: Function,
    noButton: Boolean,
  },
  watch: {
    selectedEnrollment (selectedEnrollment) {
      this.newPayment.enrollment = selectedEnrollment.id
    },
    dialog (newData, oldData) {
      if (newData) {
        // Dialog is being opened
        if (!this.newPayment.id) {
          this.newPayment.collected_by = this.admin.user.first_name
        }
      } else {
        // Dialog is being closed
        this.resetForm()
      }
    },
  },
  computed: {
    ...mapState(['admin', 'enrollments', 'student']),
    amountInWords () {
      if (! this.newPayment.amount) {
        return ''
      }
      return numberToWords.toWords(this.newPayment.amount);
    },
    oldBalance() {
      if (this.selectedEnrollment.id) {
        return (parseFloat(this.selectedEnrollment.remaining_balance))
      }
    },
    newBalance() {
      if (this.newPayment.id) {
        return (parseFloat(this.selectedEnrollment.remaining_balance) + parseFloat(this.oldPayment.amount || 0)) - this.newPayment.amount
      } else {
        return (parseFloat(this.selectedEnrollment.remaining_balance) - (this.newPayment.amount || 0)).toFixed(2)
      }
    },
  },
  data() {
    return {
      menu: false,
      dialog: false,
      isEdit: false,

      paymentMode: [
        { value: 1, text: 'Cash' },
        { value: 2, text: 'Check' }
      ],

      selectedEnrollment: {},

      newPayment: {
        id: null,
        payment_for: null,
        remarks: null,
        method: 1,
        collected_by: null,
        collected_date: new Date().toISOString().substr(0, 10),

        amount: 0,

        enrollment: null,
      },

      oldPayment: {},
    }
  },
  methods: {
    ...mapActions([
      'createPayment',
      'updatePayment',
    ]),
    resetForm () {
      this.newPayment = {
        id: null,
        payment_for: null,
        remarks: null,
        method: 1,
        collected_by: null,
        collected_date: new Date().toISOString().substr(0, 10),

        amount: 0,

        enrollment: null,
      }
      this.selectedEnrollment = {}
      this.$refs.form.resetValidation()
    },
    closeDialog() {
      this.resetForm()
      this.dialog = false
    },
    addPayment() {
      const data = Object.assign(this.newPayment)
      this.createPayment(data)
        .then(() => {
          this.successCallback()
          this.closeDialog()
        })
        .catch((errors) => {
          this.$toastr('error', Object.values(errors), 'Error')
        })
    },
    editPayment() {
      const data = Object.assign(this.newPayment)
      this.updatePayment(data)
        .then(() => {
          this.successCallback()
          this.closeDialog()
        })
        .catch((errors) => {
          this.$toastr('error', Object.values(errors), 'Error')
        })
    },
    submitPaymentForm() {
      if (this.$refs.form.validate()) {
        this.newPayment.id ? this.editPayment() : this.addPayment()
      }
    },
  },
}
</script>
